// Hand-drawn 16px line icons (1.5 stroke, round joins). Original shapes; currentColor only.
import type { ReactNode } from "react";
import { BRAND } from "../brand.ts";
import type { Glyph } from "../items.ts";

export type IconName =
  | "write"
  | "breakdown"
  | "visualize"
  | Glyph
  | "chevron"
  | "settings"
  | "grid"
  | "list"
  | "search"
  | "plus"
  | "back"
  | "forward"
  | "close"
  | "menu"
  | "spark"
  | "check"
  | "upload"
  | "board"
  | "layers"
  | "people"
  | "bell"
  | "eye"
  | "comment"
  | "link"
  | "copy"
  | "dot"
  | "home"
  | "create"
  | "productions"
  | "explore"
  | "learn"
  | "remix"
  | "recreate"
  | "play"
  | "pause"
  | "info"
  | "lock"
  | "arrow"
  | "file"
  | "image"
  | "video"
  | "at"
  | "animate"
  | "frame"
  | "clock"
  | "model"
  | "quality"
  | "minus"
  | "more"
  | "archive"
  | "camera"
  | "docs"
  | "external"
  | "castAdd"
  | "grip"
  | "loop"
  | "volume"
  | "muted"
  | "fullscreen";

const P: Record<IconName, ReactNode> = {
  folder: (
    <path d="M2 4.5c0-.55.45-1 1-1h3.2l1.3 1.5H13c.55 0 1 .45 1 1v6.5c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1z" />
  ),
  episode: (
    <>
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <path d="M2 6h12M5 3v3M8 3v3M11 3v3" />
    </>
  ),
  scene: (
    <>
      <rect x="2" y="4" width="12" height="9" rx="1" />
      <path d="M2 4l2-2h8l2 2" />
    </>
  ),
  shot: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1" />
      <path d="M6.5 6.2v3.6L9.6 8z" />
    </>
  ),
  character: (
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M3.5 13.5c.6-2.4 2.4-3.6 4.5-3.6s3.9 1.2 4.5 3.6" />
    </>
  ),
  location: (
    <>
      <path d="M8 14s4.5-4.1 4.5-7.5a4.5 4.5 0 0 0-9 0C3.5 9.9 8 14 8 14z" />
      <circle cx="8" cy="6.5" r="1.5" />
    </>
  ),
  prop: (
    <>
      <path d="M8 2l5.5 3v6L8 14l-5.5-3V5z" />
      <path d="M2.5 5L8 8l5.5-3M8 8v6" />
    </>
  ),
  style: (
    <>
      <path d="M3 13c0-3 1.5-4 3-4s2 1 2 2-1 3-5 2z" />
      <path d="M7.5 9.5l5.5-6.5" />
    </>
  ),
  elements: (
    <>
      <circle cx="5.5" cy="5.5" r="2.5" />
      <rect x="9" y="3" width="5" height="5" rx="1" />
      <path d="M2.5 13.5l3-4 3 4zM9 11.5h5M11.5 9v5" />
    </>
  ),
  canvas: (
    <>
      <rect x="2" y="2.5" width="4.5" height="4" rx="1" />
      <rect x="9.5" y="9.5" width="4.5" height="4" rx="1" />
      <path d="M6.5 4.5h3a2 2 0 0 1 2 2v3" />
    </>
  ),
  board: (
    <>
      <rect x="2" y="2.5" width="4.5" height="4" rx="1" />
      <rect x="9.5" y="9.5" width="4.5" height="4" rx="1" />
      <path d="M6.5 4.5h3a2 2 0 0 1 2 2v3" />
    </>
  ),
  asset: (
    <>
      <path d="M4 2h5l3 3v9H4z" />
      <path d="M9 2v3h3" />
    </>
  ),
  chevron: <path d="M6 4l4 4-4 4" />,
  grid: (
    <>
      <rect x="2.5" y="2.5" width="4.5" height="4.5" rx=".8" />
      <rect x="9" y="2.5" width="4.5" height="4.5" rx=".8" />
      <rect x="2.5" y="9" width="4.5" height="4.5" rx=".8" />
      <rect x="9" y="9" width="4.5" height="4.5" rx=".8" />
    </>
  ),
  list: <path d="M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.5M2.5 8h.5M2.5 12h.5" />,
  search: (
    <>
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2L13.5 13.5" />
    </>
  ),
  plus: <path d="M8 3v10M3 8h10" />,
  write: <path d="M3 13l.7-2.8 7-7a1.4 1.4 0 012 2l-7 7zM9.8 4.1l2 2" />,
  breakdown: (
    <>
      <path d="M2.5 3.5h5l6 6-4 4-6-6z" />
      <circle cx="5.3" cy="6" r=".6" />
    </>
  ),
  visualize: (
    <>
      <rect x="2" y="3" width="5" height="4" rx=".7" />
      <rect x="9" y="3" width="5" height="4" rx=".7" />
      <rect x="2" y="9" width="5" height="4" rx=".7" />
      <path d="M9 11h5M9 9.500h3.500M9 12.500h4" />
    </>
  ),
  back: <path d="M10 4L6 8l4 4" />,
  forward: <path d="M6 4l4 4-4 4" />,
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  menu: <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />,
  spark: (
    <path d="M8 2.5v3M8 10.5v3M2.5 8h3M10.5 8h3M4.2 4.2l1.6 1.6M10.2 10.2l1.6 1.6M11.8 4.2l-1.6 1.6M5.8 10.2l-1.6 1.6" />
  ),
  check: <path d="M3.5 8.5l3 3 6-7" />,
  upload: <path d="M8 11V3M5 6l3-3 3 3M3 13h10" />,
  layers: <path d="M8 2.5l5.5 3L8 8.5l-5.5-3zM2.5 8L8 11l5.5-3M2.5 10.5L8 13.5l5.5-3" />,
  people: (
    <>
      <circle cx="6" cy="5.5" r="2.2" />
      <path d="M2 13c.5-2.2 2-3.3 4-3.3s3.5 1.1 4 3.3" />
      <path d="M10.6 3.6a2.2 2.2 0 010 4.2M12 9.9c1 .5 1.7 1.5 2 3.1" />
    </>
  ),
  bell: (
    <>
      <path d="M4 11.5V7.5a4 4 0 0 1 8 0v4l1 1.2H3z" />
      <path d="M6.6 14a1.5 1.5 0 0 0 2.8 0" />
    </>
  ),
  eye: (
    <>
      <path d="M1.8 8S4 3.8 8 3.8 14.2 8 14.2 8 12 12.2 8 12.2 1.8 8 1.8 8z" />
      <circle cx="8" cy="8" r="1.8" />
    </>
  ),
  comment: (
    <path d="M3 3.5h10c.55 0 1 .45 1 1v6c0 .55-.45 1-1 1H8.5L5.5 14v-2.5H3c-.55 0-1-.45-1-1v-6c0-.55.45-1 1-1z" />
  ),
  link: (
    <>
      <path d="M6.8 9.2a2.6 2.6 0 0 0 3.7 0l2-2a2.6 2.6 0 0 0-3.7-3.7l-.7.7" />
      <path d="M9.2 6.8a2.6 2.6 0 0 0-3.7 0l-2 2a2.6 2.6 0 0 0 3.7 3.7l.7-.7" />
    </>
  ),
  /** two sheets, one over the other: copy to the clipboard (not "link") */
  copy: (
    <>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.3" />
      <path d="M10.5 3.2v-.2c0-.55-.45-1-1-1H3.5c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h.2" />
    </>
  ),
  dot: <circle cx="8" cy="8" r="2.5" />,
  home: (
    <>
      <path d="M2.5 7L8 2.5 13.5 7v6.5h-11z" />
      <path d="M6.5 13.5v-4h3v4" />
    </>
  ),
  // a viewfinder with a plus: make a take (no sparkle, BRAND.md)
  create: <path d="M2.5 5.5v-2h3M10.5 3.5h3v2M13.5 10.5v2h-3M5.5 12.5h-3v-2M8 5.5v5M5.5 8h5" />,
  productions: (
    <>
      <rect x="2" y="5" width="12" height="8.5" rx="1.5" />
      <path d="M4 3h8" />
    </>
  ),
  explore: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M10.2 5.8L9 9 5.8 10.2 7 7z" />
    </>
  ),
  learn: (
    <>
      <path d="M1.5 6.5L8 3.5l6.5 3L8 9.5z" />
      <path d="M4.5 8v3c1 1 2.2 1.5 3.5 1.5s2.5-.5 3.5-1.5V8" />
    </>
  ),
  remix: (
    <>
      <path d="M12.5 6.5A4.6 4.6 0 0 0 4 5.2M3.5 9.5A4.6 4.6 0 0 0 12 10.8" />
      <path d="M4 2.5v2.7h2.7M12 13.5v-2.7H9.3" />
    </>
  ),
  play: <path d="M5.5 3.5v9l7-4.5z" />,
  pause: <path d="M5.5 3.5v9M10.5 3.5v9" />,
  info: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 7.2v3.6M8 5.2v.1" />
    </>
  ),
  lock: (
    <>
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.2" />
      <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" />
    </>
  ),
  arrow: <path d="M3 8h10M9 4l4 4-4 4" />,
  recreate: <path d="M6 3.5l-3 3 3 3M3 6.5h6.5a3.5 3.5 0 0 1 0 7H7" />,
  settings: (
    <>
      <path d="M2.5 4.5h6M11.5 4.5h2M2.5 11.5h2M7.5 11.5h6" />
      <circle cx="10" cy="4.5" r="1.5" />
      <circle cx="6" cy="11.5" r="1.5" />
    </>
  ),
  file: (
    <>
      <path d="M4 2h5l3 3v9H4z" />
      <path d="M9 2v3h3M6 8.5h4M6 11h4" />
    </>
  ),
  image: (
    <>
      <rect x="2" y="3" width="12" height="10" rx="1.2" />
      <path d="M2.5 11.5l3.5-3.5 3 3 1.8-1.8 2.7 2.7" />
      <circle cx="10.5" cy="6" r="1" />
    </>
  ),
  video: (
    <>
      <rect x="1.8" y="4" width="9" height="8" rx="1.2" />
      <path d="M10.8 7l3.4-2v6l-3.4-2" />
    </>
  ),
  at: (
    <>
      <circle cx="8" cy="8" r="2.4" />
      <path d="M10.4 6v2.9c0 1 .7 1.6 1.5 1.6s1.6-.7 1.6-2.5A5.5 5.5 0 1 0 11 12.7" />
    </>
  ),
  /* a still with motion lines behind it: a picture that starts to move */
  animate: (
    <>
      <rect x="6" y="3.5" width="8" height="9" rx="1.2" />
      <path d="M1.8 6h2.4M1.8 8h2.4M1.8 10h2.4" />
      <path d="M6.5 10.8l2.4-2.4 1.8 1.8 1.1-1.1 1.7 1.7" />
    </>
  ),
  frame: (
    <>
      <path d="M2.5 5.5v-2h3M10.5 3.5h3v2M13.5 10.5v2h-3M5.5 12.5h-3v-2" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 5v3.2l2 1.3" />
    </>
  ),
  /** a model: three bars of rising height */
  model: <path d="M4 12V9M8 12V6M12 12V3.5" />,
  /** output quality: a cut gem */
  quality: <path d="M4.5 3h7L14 6.3 8 13 2 6.3zM2 6.3h12M6.2 3 5.4 6.3 8 13l2.6-6.7L9.8 3" />,
  minus: <path d="M3.5 8h9" />,
  more: <path d="M3.5 8h.01M8 8h.01M12.5 8h.01" strokeWidth="2" />,
  archive: (
    <>
      <rect x="2" y="3" width="12" height="3" rx="0.8" />
      <path d="M3 6v6.2c0 .45.35.8.8.8h8.4c.45 0 .8-.35.8-.8V6M6.5 9h3" />
    </>
  ),
  camera: (
    <>
      <rect x="1.8" y="4.5" width="9" height="7" rx="1.2" />
      <path d="M10.8 7.2l3.4-1.8v5.2l-3.4-1.8" />
    </>
  ),
  /** an open book: the public docs */
  docs: (
    <path d="M8 4.5C6.7 3.6 4.9 3.2 2.5 3.2v9.3c2.4 0 4.2.4 5.5 1.3 1.3-.9 3.1-1.3 5.5-1.3V3.2c-2.4 0-4.2.4-5.5 1.3zM8 4.5v9.3" />
  ),
  /** leaves the studio: opens in a new tab */
  external: (
    <path d="M7 3.5H4.3c-.45 0-.8.35-.8.8v7.4c0 .45.35.8.8.8h7.4c.45 0 .8-.35.8-.8V9M9.5 3.5h3v3M12.5 3.5 7.5 8.5" />
  ),
  /* a person with a plus: put cast, a set or a prop in the prompt (the @ menu) */
  castAdd: (
    <>
      <circle cx="6.5" cy="5.5" r="2.5" />
      <path d="M2 13.5c.4-2.4 2.2-4 4.5-4s4.1 1.6 4.5 4M12.5 4.5v4M10.5 6.5h4" />
    </>
  ),
  /** a handle to drag a row by: two columns of three dots */
  grip: <path d="M6 4h.01M10 4h.01M6 8h.01M10 8h.01M6 12h.01M10 12h.01" strokeWidth="2" />,
  loop: (
    <path d="M11 2l2.5 2.5L11 7M13 4.5H5.5A3 3 0 0 0 2.5 7.5M5 14l-2.5-2.5L5 9M3 11.5h7.5a3 3 0 0 0 3-3" />
  ),
  volume: <path d="M2.5 6v4h2.5l3.5 3V3L5 6zM11 6a3 3 0 0 1 0 4M12.8 4a5.5 5.5 0 0 1 0 8" />,
  muted: <path d="M2.5 6v4h2.5l3.5 3V3L5 6zM11 6.25l3 3.5M14 6.25l-3 3.5" />,
  fullscreen: <path d="M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10" />,
};

export function Icon({ name, size = 16, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={`icon${className ? ` ${className}` : ""}`}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {P[name]}
    </svg>
  );
}

/** The product mark: a frame inside a square, nothing borrowed. */
/** The nine departments in production order, row by row (Writing … Final). */
export const MARK_SQUARES = [
  "writing",
  "breakdown",
  "casting",
  "wardrobe",
  "sets",
  "props",
  "animatic",
  "draft",
  "final",
] as const;

/** Another brand's mark: its image as a mask, in the current text colour. */
export function BrandMark({ src, size, className = "" }: { src: string; size: number; className?: string }) {
  return (
    <span
      className={`brand-mark ${className}`}
      aria-hidden="true"
      style={{ width: size, height: size, maskImage: `url(${src})` }}
    />
  );
}

/** The logo: a 3×3 grid of nine rounded squares, one per department in its colour. */
export function Mark({ size = 22 }: { size?: number }) {
  if (BRAND.mark) return <BrandMark src={BRAND.mark} size={size} />;
  return (
    <svg width={size} height={size} viewBox="0 0 22 22" aria-hidden="true" focusable="false">
      {MARK_SQUARES.map((d, i) => (
        <rect
          key={d}
          x={(i % 3) * 8}
          y={Math.floor(i / 3) * 8}
          width="6"
          height="6"
          rx="1.5"
          fill={`var(--dept-${d})`}
        />
      ))}
    </svg>
  );
}
